<template>
	<div ref="legendContent" class="embedded-genogram-legend" v-if="hasLegendItemsToShow">
		<!--
		<div class="embedded-genogram-legend__header">
			<div class="embedded-genogram-legend__header-top">
				<h3 class="embedded-genogram-legend__title">Interactive legend</h3>
			</div>
			<div class="embedded-genogram-legend__toolbar">
				<qw-link-button v-if="hasLockedLegendItemsComputed" @click="clearLockedLegendItem">Clear selection</qw-link-button>
				<div v-else class="embedded-genogram-legend__helper">{{ legendHelperTextComputed }}</div>
			</div>
		</div>
		-->
		<div class="embedded-genogram-legend__columns">
			<EmbeddedGenogramLegendColumn
				v-for="column in visibleColumns"
				:key="column.id"
				:title="column.title"
				:rows="column.rows"
				:is-hoverable="isCyAvailableComputed"
				@hover-start="onLegendHoverStart"
				@hover-end="onLegendHoverEnd"
				@toggle-lock="onLegendLockToggle"
			/>
		</div>
	</div>
</template>

<script>
import EmbeddedGenogramLegendColumn from "./EmbeddedGenogramLegendColumn.vue";
import {
	findEmotionalLineTypeItemByLineType,
	findFamilyLineTypeItemByLineType,
	resolveEmotionalLineTypeItemByLineType,
} from "@/core/recoveryCoachingApp/coach/clients/ClientDetails/Genogram/GenogramEditor/lib/cyeditor-edit-elements/LineTypeItem";
import {
	nodeTypeToImage,
	nodeTypesSymbols,
} from "@/core/recoveryCoachingApp/coach/clients/ClientDetails/Genogram/GenogramEditor/defaults/node-types";
import {getData} from "@/core/recoveryCoachingApp/coach/clients/ClientDetails/Genogram/GenogramEditor/lib/genogramUtils";
import GenogramItem from "@shared/Genogram/GenogramItem";
import {
	isPrimaryPersonNodeData,
	PRIMARY_PERSON_LABEL,
} from "@/core/recoveryCoachingApp/coach/clients/ClientDetails/Genogram/GenogramEditor/lib/GenogramPrimaryPersonHelper";
import {buildPassedAwayLegendItems} from "@/core/recoveryCoachingApp/coach/clients/ClientDetails/Genogram/GenogramEditor/lib/GenogramLegendSpecialSymbolHelper";
import {
	applyLegendInteractionState,
	clearLegendHover,
	getLegendHoverItemKey,
	LEGEND_HOVER_KIND,
} from "@/core/recoveryCoachingApp/coach/clients/ClientDetails/Genogram/GenogramEditor/lib/GenogramLegendHoverHelper";
import GenogramCustomConnectionsSyncManager from "@/core/recoveryCoachingApp/coach/clients/ClientDetails/Genogram/GenogramEditor/lib/GenogramCustomConnectionsSyncManager";
import {
	buildHealthConditionLegendItems,
	formatLegendItemCountText,
	shouldShowLegendItemCount,
} from "@/core/recoveryCoachingApp/coach/clients/ClientDetails/Genogram/GenogramEditor/lib/GenogramLegendCountHelper";
import {
	getSexualCommunicationLegendLabel,
	hasSexualCommunicationValue,
} from "@/core/recoveryCoachingApp/coach/clients/ClientDetails/Genogram/GenogramEditor/lib/GenogramSexualCommunicationHelper";

let html2canvasImport = () => import("html2canvas");

export default {
	name: "EmbeddedGenogramLegendView",
	components: {EmbeddedGenogramLegendColumn},

	props: {
		genogramItem: {GenogramItem, required: true},
		cy: {type: Object, default: null},
		customConnectionsSyncManager: {type: GenogramCustomConnectionsSyncManager, default: null},
	},

	data() {
		return {
			hoveredLegendItemKey: "",
			lockedLegendItems: [],
		}
	},

	beforeUnmount() {
		this.clearLegendHoverState()
	},

	methods: {
		buildConnectionLegend(searchFunction) {
			let items = []

			for (let edge of this.edges) {
				let lineSubType = getData(edge, "lineSubType")
				let lineTypeItem = searchFunction(lineSubType)
				if (!lineTypeItem) {
					continue
				}

				let existingItem = items.find((item) => item.lineTypeItem.lineType === lineTypeItem.lineType)
				if (existingItem) {
					existingItem.count++
					continue
				}

				items.push({
					lineTypeItem,
					count: 1,
				})
			}

			return items.sort((left, right) => left.lineTypeItem.title.localeCompare(right.lineTypeItem.title))
		},

		buildHealthConditionsLegend() {
			return buildHealthConditionLegendItems(this.nodes, (node) => getData(node))
		},

		buildSymbolsLegend() {
			let items = []

			for (let node of this.nodes) {
				if (getData(node, "isAuxNode") || getData(node, "excludeFromLegend")) {
					continue
				}

				let type = getData(node, "type")
				let isPassedAway = !!getData(node, "isPassedAway")
				if(type === "triangle" && isPassedAway){
					continue
				}
				let symbolDefinition = nodeTypesSymbols.find((nodeType) => nodeType.type === type)
				let src = nodeTypeToImage[type]
				if (!symbolDefinition || !src) {
					continue
				}

				let existingItem = items.find((item) => item.type === type)
				if (existingItem) {
					existingItem.count++
				} else {
					items.push({
						type,
						src,
						title: symbolDefinition.label.replaceAll("<br/>", " "),
						count: 1,
					})
				}
				this.addSexualCommunicationLegendItem(items, getData(node, "sexualCommunication"))
			}

			return items.sort((left, right) => left.title.localeCompare(right.title))
		},

		addSexualCommunicationLegendItem(items, sexualCommunication) {
			if(!hasSexualCommunicationValue(sexualCommunication)){
				return
			}

			let existingItem = items.find((item) => item.sexualCommunication === sexualCommunication)
			if(existingItem){
				existingItem.count++
				return
			}

			items.push({
				type: "",
				src: "",
				title: getSexualCommunicationLegendLabel(sexualCommunication),
				count: 1,
				sexualCommunication,
			})
		},

		buildPrimaryPersonLegendItem() {
			let primaryNode = this.nodes.find((node) => {
				if(getData(node, "isAuxNode") || getData(node, "excludeFromLegend")){
					return false
				}
				return isPrimaryPersonNodeData(getData(node))
			})
			if(!primaryNode){
				return null
			}

			let type = getData(primaryNode, "type")
			let src = nodeTypeToImage[type]
			if(!src){
				return null
			}

			return {
				type,
				src,
				title: PRIMARY_PERSON_LABEL,
				isPrimaryPerson: true,
			}
		},

		getLineTypeImageSrc(lineTypeItem) {
			if(lineTypeItem.previewSrc){
				return lineTypeItem.previewSrc
			}
			return require(`./assets/images/line-${lineTypeItem.getLineTypeFull()}.png`)
		},

		async requestSnapshot() {
			await this.$nextTick()
			let container = this.$refs.legendContent
			if (!container) {
				return null
			}

			let html2canvas = (await html2canvasImport()).default
			let canvas = await html2canvas(container, {scale: 2 * window.devicePixelRatio})
			return canvas.toDataURL("image/jpeg", 1)
		},

		buildPassedAwayLegendItems() {
			return buildPassedAwayLegendItems(this.nodes, (node) => getData(node))
		},
		onLegendHoverStart(hoverItem) {
			if(!this.cy){
				return
			}

			this.hoveredLegendItemKey = this.getLegendItemKey(hoverItem)
			this.applyLegendInteractionState()
		},
		onLegendHoverEnd() {
			if(!this.cy){
				return
			}

			this.hoveredLegendItemKey = ""
			this.applyLegendInteractionState()
		},
		onLegendLockToggle(hoverItem) {
			if(!this.cy){
				return
			}

			let nextKey = this.getLegendItemKey(hoverItem)
			let existingIndex = this.lockedLegendItems.findIndex((item) => this.getLegendItemKey(item) === nextKey)
			if(existingIndex >= 0){
				this.lockedLegendItems.splice(existingIndex, 1)
			} else {
				this.lockedLegendItems.push(hoverItem)
			}
			this.hoveredLegendItemKey = ""
			this.applyLegendInteractionState()
		},
		clearLockedLegendItem() {
			this.lockedLegendItems = []
			this.hoveredLegendItemKey = ""
			if(this.cy){
				clearLegendHover(this.cy)
			}
		},
		clearLegendHoverState() {
			this.clearLockedLegendItem()
		},
		getLegendItemKey(hoverItem) {
			return getLegendHoverItemKey(hoverItem)
		},
		applyLegendInteractionState() {
			if(!this.cy){
				return
			}

			applyLegendInteractionState(this.cy, {
				hoveredItem: this.hoveredLegendItemComputed,
				lockedItems: this.lockedLegendItems,
			})
		},
		mapLegendRows(rows = []) {
			return rows.map((row) => {
				let rowKey = this.getLegendItemKey(row.hoverItem)
				return {
					...row,
					isLocked: !!rowKey && this.lockedLegendItemKeysComputed.includes(rowKey),
					isHovered: !!rowKey && rowKey === this.hoveredLegendItemKey,
				}
			})
		},
		formatSymbolLegendText(item, shouldShowSymbolsCount) {
			return formatLegendItemCountText(item.title, item.count, shouldShowSymbolsCount)
		},
		formatConnectionLegendText(item, shouldShowConnectionCount) {
			return formatLegendItemCountText(item.lineTypeItem.title, item.count, shouldShowConnectionCount)
		},
	},

	computed: {
		legendHelperTextComputed() {
			return "Use the legend to explore patterns"
		},
		elements() {
			return this.genogramItem?.jsonData?.elements || {}
		},
		nodes() {
			return Array.isArray(this.elements.nodes) ? this.elements.nodes : []
		},
		edges() {
			return Array.isArray(this.elements.edges) ? this.elements.edges : []
		},
		groupsRows() {
			let nodeGroups = Array.isArray(this.genogramItem?.nodeGroups) ? this.genogramItem.nodeGroups : []
			let groupItems = nodeGroups
				.filter((group) => (group.nodeIds || []).length >= 2)
				.map((group, index) => ({
					color: group.color,
					title: (group.title || "").trim() || `Group ${index + 1}`,
					nodeIds: group.nodeIds || [],
				}))
				.sort((left, right) => left.title.localeCompare(right.title))

			return groupItems.map((item) => ({
				rowType: "dot",
				text: item.title,
				color: item.color,
				hoverItem: {
					kind: LEGEND_HOVER_KIND.GROUP,
					nodeIds: item.nodeIds || [],
				},
			}))
		},
		symbolRows() {
			let primaryItem = this.buildPrimaryPersonLegendItem()
			let symbolItems = this.buildSymbolsLegend()
			let passedAwayItems = this.buildPassedAwayLegendItems()
			let shouldShowSymbolsCount = shouldShowLegendItemCount([...symbolItems, ...passedAwayItems])
			let items = []
			if(primaryItem){
				items.push({
					rowType: "symbol",
					text: primaryItem.title,
					src: primaryItem.src,
					symbolType: primaryItem.type,
					isPrimaryPerson: true,
					hoverItem: {kind: LEGEND_HOVER_KIND.PRIMARY_PERSON},
				})
			}
			items.push(...symbolItems.map((item) => ({
				rowType: "symbol",
				text: this.formatSymbolLegendText(item, shouldShowSymbolsCount),
				src: item.src,
				symbolType: item.type,
				sexualCommunication: item.sexualCommunication,
				hoverItem: {
					kind: item.sexualCommunication ? LEGEND_HOVER_KIND.SEXUAL_COMMUNICATION : LEGEND_HOVER_KIND.SYMBOL_TYPE,
					symbolType: item.type,
					sexualCommunication: item.sexualCommunication,
				},
			})))
			items.push(...passedAwayItems.map((item) => ({
				rowType: "symbol",
				text: this.formatSymbolLegendText(item, shouldShowSymbolsCount),
				src: item.src || "",
				symbolType: item.type,
				isPassedAway: item.isPassedAway,
				hoverItem: {
					kind: item.type === "triangle" ? LEGEND_HOVER_KIND.PASSED_AWAY_PREGNANCY : LEGEND_HOVER_KIND.PASSED_AWAY,
				},
			})))
			return this.mapLegendRows(items)
		},
		familyRows() {
			let connectionItems = this.buildConnectionLegend(findFamilyLineTypeItemByLineType)
			let shouldShowConnectionCount = connectionItems.some((item) => item.count > 1)
			return this.mapLegendRows(connectionItems.map((item) => ({
				rowType: "line",
				text: this.formatConnectionLegendText(item, shouldShowConnectionCount),
				src: this.getLineTypeImageSrc(item.lineTypeItem),
				isFamilyLine: true,
				hoverItem: {
					kind: LEGEND_HOVER_KIND.LINE_SUB_TYPE,
					lineSubType: item.lineTypeItem.lineType,
				},
			})))
		},
		emotionalRows() {
			let connectionItems = this.buildConnectionLegend((lineType) => resolveEmotionalLineTypeItemByLineType(lineType, this.customConnectionsSyncManager))
			let shouldShowConnectionCount = connectionItems.some((item) => item.count > 1)
			return this.mapLegendRows(connectionItems.map((item) => ({
				rowType: "line",
				text: this.formatConnectionLegendText(item, shouldShowConnectionCount),
				src: this.getLineTypeImageSrc(item.lineTypeItem),
				isFamilyLine: false,
				hoverItem: {
					kind: LEGEND_HOVER_KIND.LINE_SUB_TYPE,
					lineSubType: item.lineTypeItem.lineType,
				},
			})))
		},
		healthRows() {
			let healthItems = this.buildHealthConditionsLegend()
			let shouldShowHealthConditionsCount = shouldShowLegendItemCount(healthItems)
			return this.mapLegendRows(healthItems.map((item) => ({
				rowType: "dot",
				text: formatLegendItemCountText(item.title, item.count, shouldShowHealthConditionsCount),
				color: item.color,
				hoverItem: {
					kind: LEGEND_HOVER_KIND.HEALTH_CONDITION,
					title: item.title,
					color: item.color,
				},
			})))
		},
		isCyAvailableComputed() {
			return !!this.cy
		},
		hoveredLegendItemComputed() {
			return this.visibleColumns
				.flatMap((column) => column.rows)
				.find((row) => this.getLegendItemKey(row.hoverItem) === this.hoveredLegendItemKey)?.hoverItem || null
		},
		lockedLegendItemKeysComputed() {
			return this.lockedLegendItems.map((item) => this.getLegendItemKey(item))
		},
		hasLockedLegendItemsComputed() {
			return this.lockedLegendItems.length > 0
		},
		visibleColumns() {
			return [
				{id: "symbols", title: "Symbols", rows: this.symbolRows},
				{id: "groups", title: "Groups", rows: this.mapLegendRows(this.groupsRows)},
				{id: "family", title: "Family Relationships", rows: this.familyRows},
				{id: "emotional", title: "Emotional relationships", rows: this.emotionalRows},
				{id: "health", title: "Health conditions", rows: this.healthRows},
			].filter((column) => column.rows.length > 0)
		},
		hasLegendItemsToShow() {
			return this.visibleColumns.length > 0
		},
	},
}
</script>

<style lang="scss">
.embedded-genogram-legend{
	flex: 0 0 auto;
	padding: 14px 18px 16px;
	border-top: 1px solid #e9e9e9;
	background: #fff;
}

.embedded-genogram-legend__header{
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 2px;
	margin-bottom: 14px;
	padding-bottom: 8px;
	border-bottom: 1px solid #e9eef5;
}

.embedded-genogram-legend__header-top{
	width: 100%;
}

.embedded-genogram-legend__title{
	margin: 0;
	font-size: 14px;
	font-weight: 700;
	line-height: 1.2;
	color: #243b53;
}

.embedded-genogram-legend__toolbar{
	display: flex;
	align-items: center;
	justify-content: flex-start;
	line-height: 1;
	min-height: 21px;
}

.embedded-genogram-legend__toolbar .link-button{
	display: inline-flex;
	align-items: center;
	line-height: 1.5;
	font-size: 14px;
}

.embedded-genogram-legend__helper{
	font-size: 14px;
	line-height: 1.5;
	color: #7b8794;
	text-align: left;
	max-width: 100%;
}

.embedded-genogram-legend__columns{
	display: flex;
	flex-direction: row;
	flex-wrap: wrap;
	justify-content: space-around;
	column-gap: 12px;
	row-gap: 20px;
	align-items: start;
}

@media (max-width: 1100px){
	.embedded-genogram-legend{
		max-height: 34vh;
		overflow-y: auto;
	}
}

</style>
